import React, { useMemo, useState } from 'react';
import { View, Pressable } from 'react-native';
import { ThemedText } from '@/components/themed-text';
import { Card, CardContent } from '@/components/ui/card';
import { PageLayout } from '@/components/ui/page-layout';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useToastStore } from '@/store/toastStore';
import { getApiErrorMessage } from '@/utils/api-error';
import { Period, PeriodSelector } from '@/components/dashboard/period-selector';
import { ExpenseModal } from '@/components/expenses/expense-modal';
import { useExpenses, useExpensesTotal, useDeleteExpense } from '@/hooks/use-expenses';
import { Expense } from '@/types/expense';
import { EXPENSE_CATEGORY_LABELS, type ExpenseCategory } from '@/types/expense';
import { formatCurrency } from '@/utils/format-currency';
import { format, subDays, startOfDay, endOfDay, startOfMonth, endOfMonth } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Ionicons } from '@expo/vector-icons';
import { ErrorState } from '@/components/ui/error-state';
import { EmptyState } from '@/components/ui/empty-state';
import { SkeletonListRow } from '@/components/ui/skeleton';
import { FadeInView } from '@/components/ui/fade-in-view';
import { cn } from '@/utils/cn';

export default function ExpensesScreen() {
  const [period, setPeriod] = useState<Period>('30d');
  const [customRange, setCustomRange] = useState<{ start: Date; end: Date }>({
    start: subDays(new Date(), 30),
    end: new Date(),
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [expenseToDelete, setExpenseToDelete] = useState<Expense | null>(null);
  const { addToast } = useToastStore();

  const { startDate, endDate } = useMemo(() => {
    let start: Date;
    let end: Date = new Date();
    if (period === 'custom') {
      start = startOfDay(customRange.start);
      end = endOfDay(customRange.end);
    } else if (period === 'all') {
      start = subDays(new Date(), 365);
      end = endOfDay(new Date());
    } else if (period === 'month') {
      start = startOfMonth(new Date());
      end = endOfMonth(new Date());
    } else {
      const days = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      end = endOfDay(new Date());
      start = startOfDay(subDays(end, days - 1));
    }
    return {
      startDate: format(start, 'yyyy-MM-dd'),
      endDate: format(end, 'yyyy-MM-dd'),
    };
  }, [period, customRange]);

  const {
    data: listData,
    isLoading,
    error,
    refetch,
  } = useExpenses({
    startDate,
    endDate,
    page: 1,
    limit: 100,
  });
  const { data: totalAmount } = useExpensesTotal(startDate, endDate);
  const deleteMutation = useDeleteExpense();

  const expenses = listData?.data ?? [];
  const total = totalAmount ?? 0;

  const handleEdit = (expense: Expense) => {
    setEditingExpense(expense);
    setModalOpen(true);
  };

  const handleDelete = (expense: Expense) => setExpenseToDelete(expense);

  const deleteExpense = async () => {
    if (!expenseToDelete) return;
    try {
      await deleteMutation.mutateAsync(expenseToDelete.id);
      refetch();
    } catch (error) {
      addToast(getApiErrorMessage(error, 'Não foi possível excluir o gasto.'), 'error');
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditingExpense(null);
  };

  const handleSuccess = () => {
    refetch();
    handleCloseModal();
  };

  return (
    <>
      <PageLayout
        title="Gastos"
        subtitle="Seus custos sob controle"
        buttonTitle="Lançar gasto"
        onButtonPress={() => {
          setEditingExpense(null);
          setModalOpen(true);
        }}
      >
        <PeriodSelector
          noCard
          label="Período"
          period={period}
          onPeriodChange={setPeriod}
          customRange={customRange}
          onCustomRangeChange={setCustomRange}
        />

        {!isLoading && !error && (
          <FadeInView delay={0}>
            <View
              className="flex-row items-center justify-between rounded-2xl bg-ink px-4 py-3.5"
              style={{ borderCurve: 'continuous' }}
            >
              <ThemedText className="font-medium text-sm text-white/60">
                Total no período
              </ThemedText>
              <ThemedText
                className="font-bold text-lg text-white"
                style={{ fontVariant: ['tabular-nums'] }}
              >
                {formatCurrency(total)}
              </ThemedText>
            </View>
          </FadeInView>
        )}

        {isLoading ? (
          <View className="gap-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <SkeletonListRow key={i} />
            ))}
          </View>
        ) : error ? (
          <ErrorState
            variant="inline"
            title="Erro ao carregar gastos"
            message="Não foi possível carregar a lista de gastos."
            onRetry={refetch}
          />
        ) : expenses.length === 0 ? (
          <EmptyState
            icon="wallet-outline"
            title="Nenhum gasto no período"
            description="Cadastre um gasto para acompanhar seus custos."
            actionLabel="Novo gasto"
            onAction={() => {
              setEditingExpense(null);
              setModalOpen(true);
            }}
            className="py-12"
          />
        ) : (
          <Card>
            <CardContent className="pb-2 pt-2">
              {expenses.map((item, index) => (
                <FadeInView key={item.id} delay={index * 30}>
                  <ExpenseRow
                    expense={item}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    isLast={index === expenses.length - 1}
                  />
                </FadeInView>
              ))}
            </CardContent>
          </Card>
        )}
      </PageLayout>

      <ExpenseModal
        open={modalOpen}
        onOpenChange={(open) => !open && handleCloseModal()}
        mode={editingExpense ? 'edit' : 'create'}
        expense={editingExpense}
        onSuccess={handleSuccess}
      />

      <ConfirmDialog
        open={expenseToDelete !== null}
        onOpenChange={(open) => !open && setExpenseToDelete(null)}
        title="Excluir gasto"
        message={
          expenseToDelete
            ? `Excluir gasto de ${formatCurrency(expenseToDelete.amount)} em ${format(new Date(expenseToDelete.date), "d 'de' MMM.", { locale: ptBR })}?`
            : ''
        }
        confirmLabel="Excluir"
        destructive
        onConfirm={deleteExpense}
      />
    </>
  );
}

function ExpenseRow({
  expense,
  onEdit,
  onDelete,
  isLast,
}: {
  expense: Expense;
  onEdit: (e: Expense) => void;
  onDelete: (e: Expense) => void;
  isLast: boolean;
}) {
  const dateFormatted = format(new Date(expense.date), "d 'de' MMM. 'de' yyyy", { locale: ptBR });
  const categoryLabel = expense.category
    ? EXPENSE_CATEGORY_LABELS[expense.category as ExpenseCategory]
    : null;

  return (
    <View
      className={cn('flex-row items-center gap-3 py-3.5', !isLast && 'border-b border-neutral-100')}
    >
      <View className="h-10 w-10 items-center justify-center rounded-full bg-error-50 dark:bg-error-500">
        <Ionicons className="text-error-600 dark:text-white" name="arrow-up-circle-outline" size={18} />
      </View>

      <View className="min-w-0 flex-1 gap-0.5">
        <View className="flex-row items-center gap-2">
          <ThemedText
            className="font-semibold text-sm text-foreground"
            style={{ fontVariant: ['tabular-nums'] }}
          >
            {formatCurrency(expense.amount)}
          </ThemedText>
          {categoryLabel && (
            <View className="rounded-full bg-neutral-100 px-2 py-0.5">
              <ThemedText className="font-semibold text-[10px] text-neutral-600">
                {categoryLabel}
              </ThemedText>
            </View>
          )}
        </View>
        <ThemedText className="text-xs text-neutral-500">{dateFormatted}</ThemedText>
        {expense.description ? (
          <ThemedText className="text-xs text-neutral-400" numberOfLines={1}>
            {expense.description}
          </ThemedText>
        ) : null}
      </View>

      <View className="flex-row gap-1">
        <Pressable
          onPress={() => onEdit(expense)}
          accessibilityRole="button"
          accessibilityLabel="Editar gasto"
          hitSlop={4}
          className="h-9 w-9 items-center justify-center rounded-full bg-neutral-100 active:opacity-70"
        >
          <Ionicons className="text-neutral-700" name="pencil-outline" size={16} />
        </Pressable>
        <Pressable
          onPress={() => onDelete(expense)}
          accessibilityRole="button"
          accessibilityLabel="Excluir gasto"
          hitSlop={4}
          className="h-9 w-9 items-center justify-center rounded-full bg-error-50 dark:bg-error-500 active:opacity-70"
        >
          <Ionicons className="text-error-600 dark:text-white" name="trash-outline" size={16} />
        </Pressable>
      </View>
    </View>
  );
}
